import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const TrekTestimonials = ({ testimonials }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  if (!testimonials?.length) return null;

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === testimonials?.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials?.length - 1 : prev - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
  };

  const current = testimonials?.[currentTestimonial];

  return (
    <div className="bg-[#fafaf7] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-secondary">Trekker</span> Stories
          </h2>
          <div className="w-16 h-1 bg-secondary rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Hear from fellow adventurers who have completed this exact trek.
            Their experiences and insights will inspire and prepare you for your
            own journey.
          </p>
        </div>

        <div className="relative bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-border/50 mb-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-6">
                <Icon name="Quote" size={22} className="text-accent" />
              </div>

              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={18}
                    className={i < (current?.rating || 0) ? "text-accent fill-current" : "text-gray-200"}
                  />
                ))}
                <span className="ml-2 text-sm text-text-secondary">{current?.rating}/5 stars</span>
              </div>

              <blockquote className="text-lg text-foreground leading-relaxed mb-6 font-body">
                &ldquo;{current?.content}&rdquo;
              </blockquote>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-border/50">
                  <Image
                    src={current?.avatar}
                    alt={current?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-headline font-semibold text-foreground">{current?.name}</h4>
                  <p className="text-sm text-text-secondary">
                    {current?.location} &bull; {current?.trekDate}
                  </p>
                </div>
              </div>

              {current?.trekDuration && (
                <div className="mt-6 pt-6 border-t border-border/50">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-foreground">Trek Duration:</span>
                      <p className="text-text-secondary">{current?.trekDuration}</p>
                    </div>

                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {current?.images?.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {current?.images?.map((image, index) => (
                    <div key={index} className="aspect-square rounded-xl overflow-hidden">
                      <Image
                        src={image?.src}
                        alt={image?.alt}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              )}

              {current?.achievement && (
                <div className="bg-success/5 border border-success/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Award" size={18} className="text-success" />
                    <span className="font-semibold text-success">Trek Achievement</span>
                  </div>
                  <p className="text-sm text-text-secondary">{current?.achievement}</p>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-md rounded-full flex items-center justify-center text-text-secondary hover:text-accent transition-colors duration-200 border border-border/50"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-md rounded-full flex items-center justify-center text-text-secondary hover:text-accent transition-colors duration-200 border border-border/50"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>

        <div className="flex justify-center gap-2 mb-8">
          {testimonials?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                index === currentTestimonial
                  ? "bg-accent w-6"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials?.map((testimonial, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl p-5 border transition-all duration-200 cursor-pointer ${
                index === currentTestimonial
                  ? "border-accent shadow-md"
                  : "border-border/50 hover:border-accent/50 hover:shadow-sm"
              }`}
              onClick={() => goToTestimonial(index)}
            >
              <div className="flex items-center gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={14}
                    className={i < (testimonial?.rating || 0) ? "text-accent fill-current" : "text-gray-200"}
                  />
                ))}
              </div>

              <p className="text-text-secondary text-sm leading-relaxed mb-4 line-clamp-3">
                &ldquo;{testimonial?.content?.substring(0, 120)}...&rdquo;
              </p>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h5 className="font-semibold text-foreground text-sm">{testimonial?.name}</h5>
                  <p className="text-xs text-text-secondary">{testimonial?.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrekTestimonials;
